class MainSearch extends SearchForm {
  constructor() {
    super();
    this.allSearchInputs = document.querySelectorAll('input[type="search"]');
    this.setupEventListeners();
  }

  setupEventListeners() {
    let allSearchForms = [];
    this.allSearchInputs.forEach(input => allSearchForms.push(input.form))
    this.input.addEventListener('focus', this.onInputFocus.bind(this));
    if (allSearchForms.length < 2) return;
    allSearchForms.forEach(form =>
      form.addEventListener('reset', this.onFormReset.bind(this))
    );
    this.allSearchInputs.forEach(input =>
      input.addEventListener('input', this.onInput.bind(this))
    );
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.keepInSync('', this.input);
    }
  }

  onInput(event) {
    const target = event.target;
    this.keepInSync(target.value, target);
  }

  onInputFocus() {
    const isSmallScreen = window.innerWidth < 750;
    if (isSmallScreen) {
      this.scrollIntoView({behavior: 'smooth'});
    }
  }

  keepInSync(value, target) {
    this.allSearchInputs.forEach(input => {
      if (input !== target) {
        input.value = value;
      }
    });
  }
}

customElements.define('main-search', MainSearch);

// OC1-330: per-type "Load more" on the search results page. On page load every
// type-filtered pagination is walked once through the Section Rendering API
// (Shopify's raw counts include hide_from_storefront_search items, so only
// enumeration tells the truth). Unseen items queue per group, which gives:
// an exact result count for the meta bar, instant "Load more" appends from
// the queue, buttons hidden up front when a group has nothing more, and
// hidden group shells (data-auto-load) filled so no result type is
// unreachable. Shopify stamps search-result URLs with per-request tracking
// params, so items are deduped by pathname.
(function () {
  const groups = document.getElementById('search-groups');
  if (!groups) return;

  const term = groups.dataset.searchTerm;
  const sortBy = groups.dataset.sortBy;
  const TYPE_PARAM = {
    courses: 'product',
    careers: 'page',
    advice: 'article,page',
  };
  const CAPS = { courses: 4, careers: 3, advice: 3 };
  // Per-invocation page budget for enormous queries — the initial sweep stops
  // here (count falls back to the server-rendered one, button stays visible)
  // and every "Load more" click resumes the walk with a fresh budget.
  const MAX_WALK_PAGES = 15;

  const urlKey = (url) => (url || '').split('?')[0];
  const rendered = new Set();
  groups.querySelectorAll('[data-result-url]').forEach((li) => rendered.add(urlKey(li.dataset.resultUrl)));

  const state = {};
  const stateFor = (kind) => (state[kind] = state[kind] || { queue: [], page: 1, exhausted: false, capped: false, walking: null });

  async function walk(kind) {
    const s = stateFor(kind);
    if (s.walking) return s.walking;
    s.walking = (async () => {
      let fetched = 0;
      s.stalled = false;
      try {
        while (!s.exhausted && fetched < MAX_WALK_PAGES) {
          const url = `${window.location.pathname}` +
            `?q=${encodeURIComponent(term)}` +
            `&options%5Bprefix%5D=last` +
            `&type=${encodeURIComponent(TYPE_PARAM[kind])}` +
            `&sort_by=${encodeURIComponent(sortBy)}` +
            `&page=${s.page}` +
            `&section_id=main-search-more`;

          const response = await fetch(url);
          if (!response.ok) {
            s.stalled = true;
            break;
          }
          fetched++;

          const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
          const totalPages = parseInt(doc.querySelector('[data-search-more-pages]')?.textContent, 10) || 1;

          doc.querySelectorAll('[data-search-more-results] > li').forEach((li) => {
            if (li.dataset.resultType !== kind) return;
            const key = urlKey(li.dataset.resultUrl);
            if (rendered.has(key)) return;
            rendered.add(key);
            s.queue.push(document.importNode(li, true));
          });

          // Surface tiles the moment a page yields them — waiting for the full
          // walk left the advice/careers groups trailing the server-rendered
          // courses by several sequential fetches (OC1-330 review).
          topUp(kind);

          if (s.page >= totalPages) s.exhausted = true;
          else s.page++;
        }
      } catch (e) {
        s.stalled = true;
      }
      // capped = the page budget ran out with pages provably remaining —
      // a click can resume it. stalled = a fetch failed; retried separately.
      s.capped = !s.exhausted && !s.stalled;
      s.walking = null;
      return s;
    })();
    return s.walking;
  }

  function sectionFor(kind) {
    return groups.querySelector(`[data-search-group="${kind}"]`);
  }

  // Fill a group up to its default tile count from the queue. A no-op once the
  // group already holds its cap, so mid-walk calls never overshoot and never
  // interfere with an explicit Load-more append.
  function topUp(kind) {
    const section = sectionFor(kind);
    if (!section) return;
    const list = section.querySelector('[data-search-group-list]');
    const shortfall = (CAPS[kind] || 6) - list.children.length;
    if (shortfall > 0) appendFromQueue(kind, shortfall);
  }

  function appendFromQueue(kind, count) {
    const s = stateFor(kind);
    const section = sectionFor(kind);
    if (!section) return;
    const list = section.querySelector('[data-search-group-list]');
    let appended = 0;
    while (s.queue.length && appended < count) {
      list.appendChild(s.queue.shift());
      appended++;
    }
    if (appended > 0) section.hidden = false;
  }

  function syncMore(kind) {
    const s = stateFor(kind);
    const section = sectionFor(kind);
    if (!section) return;
    const more = section.querySelector('.search-group__more');
    const list = section.querySelector('[data-search-group-list]');
    // Visible only when a click can definitely deliver: items already queued,
    // or a budget-capped walk that a click resumes. A stalled (failed) walk
    // keeps the button hidden rather than showing one that does nothing.
    if (more) more.hidden = !s.queue.length && !s.capped;
    if (s.exhausted && !s.queue.length && list && !list.children.length) section.remove();
  }

  groups.querySelectorAll('[data-search-load-more]').forEach((button) => {
    button.addEventListener('click', async () => {
      const kind = button.dataset.searchLoadMore;
      button.disabled = true;
      button.classList.add('loading');
      try {
        await walk(kind);
        appendFromQueue(kind, CAPS[kind] || 6);
        syncMore(kind);
      } finally {
        button.disabled = false;
        button.classList.remove('loading');
      }
    });
  });

  // two instances: desktop meta strip + mobile hero (OC1-330 review)
  const countEls = document.querySelectorAll('.template-search__meta__count');
  let countIsExact = false;

  const setCount = (total) => {
    countEls.forEach((el) => {
      el.textContent = `${total} result${total === 1 ? '' : 's'}`;
      el.removeAttribute('data-pending');
      el.removeAttribute('aria-busy');
    });
  };

  // OC1-330 review: the count renders as a skeleton (no "24 results"
  // placeholder) and is filled from the main-search-count section, one light
  // request per type, well before the load-more walk finishes. Shopify's
  // combined-type count is pruned, hence per-type requests summed here.
  const fetchCount = (type) =>
    fetch(
      `${window.location.pathname}?q=${encodeURIComponent(term)}&options%5Bprefix%5D=last` +
        `&type=${encodeURIComponent(type)}&section_id=main-search-count`
    )
      .then((response) => {
        if (!response.ok) throw new Error(response.status);
        return response.text();
      })
      .then((text) => {
        const el = new DOMParser().parseFromString(text, 'text/html').querySelector('[data-search-count]');
        const count = parseInt(el?.textContent, 10);
        if (Number.isNaN(count)) throw new Error('count missing');
        return count;
      });

  const quickCount = countEls.length
    ? Promise.all(['product', 'page', 'article'].map(fetchCount))
        .then((counts) => {
          if (!countIsExact) setCount(counts.reduce((sum, count) => sum + count, 0));
          return true;
        })
        .catch(() => false)
    : Promise.resolve(false);

  // Each section settles independently the moment its own walk finishes: a
  // final top-up (tiles normally landed mid-walk already), then the (initially
  // hidden) Load-more button is revealed only when that group really has more
  // to load.
  const settle = (kind) => {
    topUp(kind);
    syncMore(kind);
  };

  const walks = Object.keys(TYPE_PARAM).map((kind) => walk(kind).then(async (s) => {
    settle(kind);
    // One delayed retry when the walk stalled on a failed fetch (rate limit,
    // proxy hiccup) so a transient error doesn't strand unseen results.
    if (s.stalled) {
      await new Promise((resolve) => setTimeout(resolve, 2000));
      s = await walk(kind);
      settle(kind);
    }
    return s;
  }));

  // Only the exact result count needs every walk: `rendered` then holds every
  // unique visible result — trust it only when no walk was cut short. It
  // normally equals the quick count; it only differs when a type has more than
  // 250 matches and the count endpoint fell back to Shopify's raw number.
  Promise.all(walks).then(async (states) => {
    if (!countEls.length) return;
    if (!states.some((s) => s.capped || s.stalled)) {
      countIsExact = true;
      setCount(rendered.size);
      return;
    }
    // Every source failed: reveal the server-rendered page-1 count rather
    // than leaving the skeleton forever.
    if (!(await quickCount)) {
      countEls.forEach((el) => {
        const fallback = parseInt(el.dataset.fallbackCount, 10);
        if (!Number.isNaN(fallback)) setCount(fallback);
      });
    }
  }).catch(() => {});
})();
